<script setup>
import { ref, computed, defineProps } from "vue";
import ListImage from "@/components/ListImage";

const props = defineProps({
  activeUserList: {
    type: Object,
    required: true,
    default: () => ({ total: 0, data: [] }),
  },
});

const shortNickname = (nickname) => {
  if (!nickname || nickname.trim() === "") return "匿名用户";
  const pureNickname = nickname
    .trim()
    .replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, "");
  return pureNickname.length > 4
    ? pureNickname.slice(0, 4) + "..."
    : pureNickname;
};

const showTip = ref(false);
const currentUser = ref({});
const tipLeft = ref(0);
const tipTop = ref(0);

const showUserTip = (user, e) => {
  if (!user) return;
  currentUser.value = {
    nickname: user.nickname || "匿名用户",
    avatar: user.avatar || "",
    phone: user.phone || "未填写",
    isOnline: user.isOnline,
  };
  showTip.value = true;
  tipLeft.value = e.clientX + 10;
  tipTop.value = e.clientY + 10;
};

const hideUserTip = () => {
  showTip.value = false;
};

const formatActiveUserList = computed(() => {
  const rawData = props.activeUserList;

  return {
    total: rawData.total || 0,
    data: rawData.data || [],
    per_page: rawData.per_page || 10,
    current_page: rawData.current_page || 1,
    last_page: rawData.last_page || 1,
  };
});

const displayUsers = computed(() => {
  const formatted = formatActiveUserList.value;
  return formatted.data.slice(0, 24).map((item) => {
    const seed = item.id || item.nickname || Math.random().toString();
    const hash = seed
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return {
      ...item,
      isOnline: hash % 100 < 30,
      id: item.id || `user_${hash}`,
    };
  });
});

// 简化在线人数计算
const onlineCount = computed(() => {
  return displayUsers.value.filter((item) => item.isOnline).length;
});
</script>

<template>
  <div class="active-userlist-container">
    <div class="empty-state" v-if="formatActiveUserList.total === 0">
      <span>暂无用户报名</span>
    </div>

    <template v-else>
      <div class="user-stats-bar">
        <el-text class="total-count">
          <span class="highlight">{{ formatActiveUserList.total }}</span
          >人已报名
        </el-text>
        <el-text class="real-time-tip" size="small">
          🔥 已有{{ formatActiveUserList.total }}人参与并享受福利
        </el-text>
      </div>

      <div class="user-grid">
        <div
          class="user-item"
          v-for="(item, index) in displayUsers"
          :key="item.id || index"
          @mouseenter="showUserTip(item, $event)"
          @mouseleave="hideUserTip()"
        >
          <div class="avatar-wrapper">
            <ListImage
              :url="item.avatar ? 'https://clubs.xinmin.cn/' + item.avatar : ''"
              :size="36"
              class="user-avatar"
              fallback="/static/default-avatar.png"
            />
            <span class="online-dot" v-if="item.isOnline"></span>
          </div>
          <span class="user-nickname">{{ shortNickname(item.nickname) }}</span>
        </div>
      </div>

      <!-- 底部活跃提示：简化条件 -->
      <div class="online-status">
        <div class="online-status-wrap">
          <span class="dot"></span>
          目前有 <span class="count">{{ onlineCount || 0 }}</span> 人在线
        </div>
        <div
          class="overflow-tip"
          v-if="formatActiveUserList.total > displayUsers.length"
        >
          +{{ formatActiveUserList.total - displayUsers.length }}
        </div>
      </div>
    </template>

    <!-- 用户悬浮提示：保留核心 -->
    <div
      class="user-tip"
      v-if="showTip"
      :style="{ left: tipLeft + 'px', top: tipTop + 'px' }"
    >
      <ListImage
        :url="
          currentUser.avatar
            ? 'https://clubs.xinmin.cn/' + currentUser.avatar
            : ''
        "
        :size="40"
        fallback="/static/default-avatar.png"
      />
      <div class="tip-info">
        <div class="tip-nickname">{{ currentUser.nickname }}</div>
        <div class="tip-phone">{{ currentUser.phone }}</div>
        <div class="tip-status" v-if="currentUser.isOnline">在线</div>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "~@/styles/mixin.less";

// 简化变量定义
@primary: #ff5722;
@lightBg: #fff8f5;
@gray: #999;

.active-userlist-container {
  width: 100%;
  margin: 5px auto;
  padding: 12px;
  background: @lightBg;
  border-radius: 8px;
  min-height: 200px;

  // 空状态样式
  .empty-state {
    text-align: center;
    padding: 40px 0;
    color: @gray;
    font-size: 14px;
  }

  // 统计栏
  .user-stats-bar {
    margin-bottom: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;

    .total-count {
      font-size: 16px;
      font-weight: 600;
      .highlight {
        color: @primary;
        font-size: 19px;
        margin: 0 3px;
      }
    }

    .real-time-tip {
      color: #666;
      margin-top: 5px;
    }
  }

  // 用户网格
  .user-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
    gap: 10px 8px;
    padding-bottom: 5px;
    min-height: 150px;

    .user-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      cursor: pointer;
      padding: 5px;
      border-radius: 6px;
      transition: all 0.2s ease;

      &:hover {
        transform: scale(1.05);
        background: rgba(255, 87, 34, 0.05);
      }

      .avatar-wrapper {
        position: relative;
        margin-bottom: 4px;

        .user-avatar .list-image-container {
          border-radius: 50%;
          border: 2px solid #fff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          width: 36px !important;
          height: 36px !important;
        }

        .online-dot {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 10px;
          height: 10px;
          background: #4cd964;
          border: 2px solid #fff;
          border-radius: 50%;
        }
      }

      .user-nickname {
        font-size: 11px;
        color: #333;
        white-space: nowrap;
        max-width: 60px;
        text-align: center;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  // 在线状态
  .online-status {
    margin-top: 10px;
    font-size: 12px;
    color: #666;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.05);

    .online-status-wrap {
      display: flex;
      align-items: center;
      gap: 5px;
    }

    .dot {
      width: 8px;
      height: 8px;
      background: #4cd964;
      border-radius: 50%;
      animation: pulse 2s infinite;
    }

    .count {
      color: @primary;
      font-weight: 500;
    }

    .overflow-tip {
      background: rgba(0, 0, 0, 0.7);
      color: white;
      font-size: 12px;
      padding: 2px 8px;
      border-radius: 10px;
      min-width: 30px;
      text-align: center;
    }
  }

  // 用户悬浮提示
  .user-tip {
    position: fixed;
    background: #fff;
    border-radius: 6px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
    padding: 10px;
    width: 200px;
    z-index: 1000;
    display: flex;
    gap: 10px;
    pointer-events: none;

    .list-image-container {
      border-radius: 50%;
      width: 40px !important;
      height: 40px !important;
    }

    .tip-info {
      flex: 1;
      .tip-nickname {
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 3px;
      }
      .tip-phone {
        font-size: 12px;
        color: #666;
      }
      .tip-status {
        font-size: 12px;
        color: #4cd964;
        margin-top: 2px;
      }
    }
  }
}

// 仅保留必要动画
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 217, 100, 0.4);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(76, 217, 100, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 217, 100, 0);
  }
}

// 移动端适配
@media (max-width: 768px) {
  .active-userlist-container .user-grid {
    grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  }
}
</style>